import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Role } from '@curbnturf/entities';
import { firstValueFrom } from 'rxjs';
import { ActivityApiService } from '../backend-api/activity-api.service';
import { AuthService } from '../auth/auth.service';

/**
 * Route guard for `/activity/edit/:id`. The server already enforces
 * ownership on every mutation (`ActivityLocationService`'s update/hide/show/
 * remove all call `ACLService.verifyLocationOwner`) — this guard exists to
 * avoid sending a non-owner into a form that will 403 on submit, not as the
 * real security boundary. Simpler than `editOwnerGuard`: no sales-rep
 * exception clause (no equivalent concept for POIs), and since
 * `GET activity-location/:id` is public, a direct fetch is enough — no
 * dedicated `checkOwnership()` service method needed.
 */
export const poiOwnerGuard: CanActivateFn = async (route, state) => {
  const auth = inject(AuthService);
  const activityApi = inject(ActivityApiService);
  const router = inject(Router);

  if (!auth.currentUser?.id) {
    return router.createUrlTree(['/auth/login'], { queryParams: { returnTo: state.url } });
  }
  const idRaw = route.paramMap.get('id');
  const id = idRaw ? Number(idRaw) : NaN;
  if (!Number.isFinite(id)) return router.createUrlTree(['/']);

  const activity = await firstValueFrom(activityApi.fetch(id));
  const owns =
    !!activity &&
    (activity.userId === auth.currentUser.id ||
      activity.user?.id === auth.currentUser.id ||
      auth.currentUser.role === Role.admin);

  return owns ? true : router.createUrlTree(['/']);
};
